import React, { useState, useRef, useEffect } from "react";
import Layout from "@/components/layout/Layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { FileText, Search, Filter, Plus, Download, Eye, Edit, Trash2, MapPin, Send, Printer } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { exportToPDF } from "@/utils/pdfExport";

interface Proposta {
  id: string;
  data_criacao: string;
  nome_cliente: string;
  email?: string;
  telefone?: string;
  tipo_instalacao: string;
  potencia_sistema: number;
  investimento_total: number;
  status: string;
  numero_proposta: string;
}

const PropostasPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [propostas, setPropostas] = useState<Proposta[]>([]);
  const [filteredPropostas, setFilteredPropostas] = useState<Proposta[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("todas");
  const [cepDialogOpen, setCepDialogOpen] = useState(false);
  const [cepInput, setCepInput] = useState("");
  const [isSearchingCep, setIsSearchingCep] = useState(false);
  const [addressData, setAddressData] = useState<{
    cep: string;
    logradouro: string;
    bairro: string;
    localidade: string;
    uf: string;
  } | null>(null);
  const [propostaPreviewId, setPropostaPreviewId] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const propostaPreviewRef = useRef<HTMLDivElement>(null);

  const loadPropostas = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('propostas')
        .select('*')
        .order('data_criacao', { ascending: false });

      if (error) {
        throw error;
      }

      if (data) {
        const transformedData: Proposta[] = data.map(item => ({
          id: item.id,
          data_criacao: new Date(item.data_criacao).toLocaleDateString('pt-BR'),
          nome_cliente: item.nome_cliente,
          tipo_instalacao: item.tipo_instalacao,
          potencia_sistema: item.potencia_sistema,
          investimento_total: item.investimento_total,
          status: 'Enviada',
          numero_proposta: item.numero_proposta
        }));
        
        setPropostas(transformedData);
        setFilteredPropostas(transformedData);
      }
    } catch (error) {
      console.error("Error fetching proposals:", error);
      toast.error("Erro ao carregar propostas");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadPropostas();
  }, []);

  const handleSearch = (term: string) => {
    setSearchTerm(term);
    if (!term) {
      setFilteredPropostas(propostas);
      return;
    }
    
    const filtered = propostas.filter(proposta => 
      proposta.nome_cliente.toLowerCase().includes(term.toLowerCase()) ||
      proposta.numero_proposta.includes(term)
    );
    
    setFilteredPropostas(filtered);
  };

  const handleFilterByStatus = (status: string) => {
    setActiveTab(status);
    
    if (status === "todas") {
      setFilteredPropostas(propostas);
      return;
    }
    
    const filtered = propostas.filter(proposta => 
      proposta.status.toLowerCase() === status.toLowerCase()
    );
    
    setFilteredPropostas(filtered);
  };

  const handleExportPDF = async (propostaId: string) => {
    setIsExporting(true);
    try {
      const { data, error } = await supabase
        .from('propostas')
        .select('*')
        .eq('id', propostaId)
        .single();

      if (error) throw error;
      
      if (data) {
        setPropostaPreviewId(propostaId);
        
        setTimeout(async () => {
          if (propostaPreviewRef.current) {
            const success = await exportToPDF(
              'proposta-preview', 
              `proposta-${data.numero_proposta}`,
              'portrait',
              `Proposta Comercial #${data.numero_proposta}`
            );
            
            if (success) {
              toast.success("Proposta exportada com sucesso");
            } else {
              toast.error("Erro ao exportar proposta");
            }
            setPropostaPreviewId(null);
          }
        }, 500);
      }
    } catch (error) {
      console.error("Error exporting proposal:", error);
      toast.error("Erro ao exportar proposta");
    } finally {
      setIsExporting(false);
    }
  };

  const handleSendProposalByEmail = async (propostaId: string) => {
    toast.success("Funcionalidade de envio por email será implementada em breve!");
  };

  const handleCepSearch = async () => {
    const cepPattern = /^\d{5}-?\d{3}$/;
    if (!cepPattern.test(cepInput)) {
      toast.error("CEP inválido. Use o formato 00000-000 ou 00000000");
      return;
    }

    setIsSearchingCep(true);
    setAddressData(null);
    
    try {
      const formattedCep = cepInput.replace("-", "");
      
      const response = await fetch(`https://viacep.com.br/ws/${formattedCep}/json/`);
      const data = await response.json();
      
      if (data.erro) {
        toast.error("CEP não encontrado");
        setAddressData(null);
      } else {
        setAddressData(data);
        toast.success("Endereço encontrado com sucesso!");
      }
    } catch (error) {
      console.error("Erro ao buscar CEP:", error);
      toast.error("Erro ao buscar o CEP. Tente novamente.");
      setAddressData(null);
    } finally {
      setIsSearchingCep(false);
    }
  };

  const openCepDialog = () => {
    setCepDialogOpen(true);
    setCepInput("");
    setAddressData(null);
  };

  const handleDeleteProposta = async (propostaId: string) => {
    if (window.confirm("Tem certeza que deseja excluir esta proposta? Esta ação não pode ser desfeita.")) {
      try {
        const { error } = await supabase
          .from('propostas')
          .delete()
          .eq('id', propostaId);
        
        if (error) throw error;
        
        const updatedPropostas = propostas.filter(p => p.id !== propostaId);
        setPropostas(updatedPropostas);
        setFilteredPropostas(
          filteredPropostas.filter(p => p.id !== propostaId)
        );
        
        toast.success("Proposta excluída com sucesso");
      } catch (error) {
        console.error("Error deleting proposal:", error);
        toast.error("Erro ao excluir proposta");
      }
    }
  };

  const PropostaPreview = () => {
    if (!propostaPreviewId) return null;
    
    return (
      <div id="proposta-preview" ref={propostaPreviewRef} style={{ position: 'absolute', left: '-9999px' }}>
        <h1>Proposta Comercial</h1>
        <p>Carregando dados da proposta...</p>
      </div>
    );
  };

  return (
    <Layout>
      <div className="container mx-auto px-4 py-6 max-w-screen-xl">
        <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-green-700 flex items-center gap-2">
              <FileText className="h-8 w-8" /> Propostas Comerciais
            </h1>
            <p className="text-gray-600 mt-2">
              Gerencie e acompanhe todas as propostas comerciais de sistemas solares
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <Button variant="outline" className="gap-2" onClick={openCepDialog}>
              <MapPin className="h-4 w-4" />
              Buscar CEP
            </Button>
            <Button asChild variant="outline" className="gap-2">
              <Link to="/solar/calculadora">
                <Plus className="h-4 w-4" />
                Nova Proposta
              </Link>
            </Button>
            <Button className="gap-2">
              <Download className="h-4 w-4" />
              Exportar
            </Button>
          </div>
        </div>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle>Gerenciamento de Propostas</CardTitle>
            <CardDescription>
              Visualize, edite e acompanhe o status de todas as propostas geradas
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                <div className="relative w-full sm:w-auto sm:min-w-[300px]">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
                  <Input
                    type="search"
                    placeholder="Buscar por cliente, email ou ID..."
                    className="pl-9"
                    value={searchTerm}
                    onChange={(e) => handleSearch(e.target.value)}
                  />
                </div>
                <div className="flex items-center gap-2 w-full sm:w-auto">
                  <Filter className="h-4 w-4 text-gray-500" />
                  <span className="text-sm text-gray-500">Filtrar por:</span>
                  <Tabs value={activeTab} onValueChange={handleFilterByStatus} className="w-full sm:w-auto">
                    <TabsList>
                      <TabsTrigger value="todas">Todas</TabsTrigger>
                      <TabsTrigger value="enviada">Enviadas</TabsTrigger>
                      <TabsTrigger value="em análise">Em análise</TabsTrigger>
                      <TabsTrigger value="aprovada">Aprovadas</TabsTrigger>
                    </TabsList>
                  </Tabs>
                </div>
              </div>

              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>ID</TableHead>
                      <TableHead>Data</TableHead>
                      <TableHead>Cliente</TableHead>
                      <TableHead className="hidden md:table-cell">Potência</TableHead>
                      <TableHead className="hidden md:table-cell">Valor</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {isLoading ? (
                      <TableRow>
                        <TableCell colSpan={7} className="h-24 text-center">
                          Carregando propostas...
                        </TableCell>
                      </TableRow>
                    ) : filteredPropostas.length > 0 ? (
                      filteredPropostas.map((proposta) => (
                        <TableRow key={proposta.id}>
                          <TableCell className="font-medium">{proposta.numero_proposta}</TableCell>
                          <TableCell>{proposta.data_criacao}</TableCell>
                          <TableCell>
                            <div>
                              <div className="font-medium">{proposta.nome_cliente}</div>
                              <div className="text-xs text-gray-500">{proposta.email || "Email não informado"}</div>
                            </div>
                          </TableCell>
                          <TableCell className="hidden md:table-cell">{proposta.potencia_sistema} kWp</TableCell>
                          <TableCell className="hidden md:table-cell">R$ {proposta.investimento_total.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</TableCell>
                          <TableCell>
                            <div className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium
                              ${proposta.status === "Aprovada" ? "bg-green-100 text-green-800" : 
                                proposta.status === "Enviada" ? "bg-blue-100 text-blue-800" : 
                                "bg-yellow-100 text-yellow-800"}`}>
                              {proposta.status}
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm">
                                  <span className="sr-only">Abrir menu</span>
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    className="h-4 w-4"
                                  >
                                    <circle cx="12" cy="12" r="1" />
                                    <circle cx="12" cy="5" r="1" />
                                    <circle cx="12" cy="19" r="1" />
                                  </svg>
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem 
                                  className="flex items-center gap-2"
                                  onClick={() => navigate(`/solar/propostas/${proposta.id}`)}
                                >
                                  <Eye className="h-4 w-4" /> Visualizar
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  className="flex items-center gap-2"
                                  onClick={() => handleExportPDF(proposta.id)}
                                >
                                  <Printer className="h-4 w-4" /> Exportar PDF
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  className="flex items-center gap-2"
                                  onClick={() => handleSendProposalByEmail(proposta.id)}
                                >
                                  <Send className="h-4 w-4" /> Enviar por Email
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  className="flex items-center gap-2"
                                  onClick={() => navigate(`/solar/propostas/edit/${proposta.id}`)}
                                >
                                  <Edit className="h-4 w-4" /> Editar
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  className="flex items-center gap-2 text-red-600"
                                  onClick={() => handleDeleteProposta(proposta.id)}
                                >
                                  <Trash2 className="h-4 w-4" /> Excluir
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={7} className="h-24 text-center">
                          Nenhuma proposta encontrada.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Dialog open={cepDialogOpen} onOpenChange={setCepDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5 text-green-600" /> Buscar Endereço por CEP
            </DialogTitle>
            <DialogDescription>
              Digite o CEP para localizar o endereço correspondente.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid gap-2">
              <Label htmlFor="cep">CEP</Label>
              <div className="flex gap-2">
                <Input
                  id="cep"
                  placeholder="00000-000"
                  value={cepInput}
                  onChange={(e) => setCepInput(e.target.value)}
                />
                <Button 
                  onClick={handleCepSearch}
                  disabled={isSearchingCep}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {isSearchingCep ? "Buscando..." : "Buscar"}
                </Button>
              </div>
              <p className="text-xs text-gray-500">
                Informe o CEP no formato 00000-000 ou 00000000
              </p>
            </div>

            {addressData && (
              <div className="mt-4 p-4 bg-green-50 rounded-md border border-green-200">
                <h3 className="text-lg font-medium text-green-800 mb-2">Endereço Encontrado</h3>
                <div className="space-y-1">
                  <p><span className="font-medium">CEP:</span> {addressData.cep}</p>
                  <p><span className="font-medium">Logradouro:</span> {addressData.logradouro}</p>
                  <p><span className="font-medium">Bairro:</span> {addressData.bairro}</p>
                  <p><span className="font-medium">Cidade:</span> {addressData.localidade}</p>
                  <p><span className="font-medium">Estado:</span> {addressData.uf}</p>
                </div>
              </div>
            )}

            <div className="flex justify-end">
              <DialogClose asChild>
                <Button variant="outline">Fechar</Button>
              </DialogClose>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <PropostaPreview />
    </Layout>
  );
};

export default PropostasPage;
